import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { trpcClient } from '../../../../../utils/trpc'

/**
 * @fileoverview Cycle Step Configuration Store
 *
 * Zustand store for managing cycle step configuration data (forms, reviewer groups).
 * Abstracts data fetching from the ManageCycleStep component.
 */

interface ReviewerGroup {
	id: string
	name: string
}

interface Form {
	id: string
	name: string
}

interface CycleStepConfigState {
	// Data
	forms: Form[]
	reviewerGroups: ReviewerGroup[]

	// Loading states
	isLoadingForms: boolean
	isLoadingReviewerGroups: boolean

	// Error handling
	error: string | null

	// Actions
	fetchForms: (organizationId: string) => Promise<void>
	fetchReviewerGroups: (cycleId: string) => Promise<void>
	clearError: () => void
	reset: () => void
}

export const useCycleStepConfigStore = create<CycleStepConfigState>()(
	devtools(
		(set, get) => ({
			// Initial state
			forms: [],
			reviewerGroups: [],
			isLoadingForms: false,
			isLoadingReviewerGroups: false,
			error: null,

			// Fetch forms for the organization
			fetchForms: async (organizationId: string) => {
				if (!organizationId) return

				// Skip if already loaded
				if (get().forms.length > 0) return

				set({ isLoadingForms: true, error: null })

				try {
					const result = await trpcClient.admin.forms.search.query({
						organizationId,
						search: '',
						page: 1,
						perPage: 100,
					})

					set({
						forms: result.data.map((f: { id: string; name: string }) => ({
							id: f.id,
							name: f.name,
						})),
						isLoadingForms: false,
					})
				} catch (error) {
					set({
						error: error instanceof Error ? error.message : 'Failed to fetch forms',
						isLoadingForms: false,
					})
				}
			},

			// Fetch reviewer groups for the cycle
			fetchReviewerGroups: async (cycleId: string) => {
				if (!cycleId) return

				// Skip if already loaded
				if (get().reviewerGroups.length > 0) return

				set({ isLoadingReviewerGroups: true, error: null })

				try {
					const result = await trpcClient.admin.cycles.reviewerGroups.search.query({
						cycleId,
						search: '',
					})

					set({
						reviewerGroups: result.data.map((g: { id: string; name: string }) => ({
							id: g.id,
							name: g.name,
						})),
						isLoadingReviewerGroups: false,
					})
				} catch (error) {
					set({
						error: error instanceof Error ? error.message : 'Failed to fetch rater groups',
						isLoadingReviewerGroups: false,
					})
				}
			},

			clearError: () => set({ error: null }),

			reset: () =>
				set({
					forms: [],
					reviewerGroups: [],
					isLoadingForms: false,
					isLoadingReviewerGroups: false,
					error: null,
				}),
		}),
		{ name: 'cycle-step-config-store' },
	),
)
